import React, { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { Check, ChevronDown, Globe } from 'lucide-react';
import { LANGUAGES, useI18n } from '../../i18n';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

/**
 * Compact language picker: a globe plus the current code, opening a small
 * menu. It keeps one fixed footprint at every width, where the old four-pill
 * segmented control wrapped into a tall column in a crowded header.
 *
 * `direction="up"` opens the menu above the trigger, for spots near the
 * bottom of the screen such as the mobile drawer footer.
 */
const LanguageSwitcher = ({ className = '', direction = 'down', align = 'right' }) => {
  const { lang, setLang, t } = useI18n();
  const current = LANGUAGES.find((item) => item.code === lang) || LANGUAGES[0];

  return (
    <Menu as="div" className={classNames('relative inline-block shrink-0 text-left', className)}>
      {({ open }) => (
        <>
          <Menu.Button
            aria-label={`${t('common.language')}: ${current.name}`}
            title={t('common.language')}
            className={classNames(
              'notranslate inline-flex h-10 items-center gap-1.5 rounded-full border px-3 text-xs font-bold uppercase tracking-[0.14em] transition',
              open
                ? 'border-[#b87ff0]/50 bg-white/10 text-white'
                : 'border-white/10 bg-white/5 text-white/80 hover:bg-white/10 hover:text-white'
            )}
          >
            <Globe className="h-4 w-4 shrink-0 text-white/60" aria-hidden="true" />
            <span>{current.label}</span>
            <ChevronDown
              className={classNames(
                'h-3.5 w-3.5 shrink-0 transition-transform duration-200',
                open ? 'rotate-180 text-brand-soft' : 'text-white/50'
              )}
              aria-hidden="true"
            />
          </Menu.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-150"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-100"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items
              className={classNames(
                'absolute z-50 w-44 rounded-2xl border border-white/10 bg-[#0f0a14]/95 p-1.5 shadow-2xl shadow-black/60 backdrop-blur-xl focus:outline-none',
                direction === 'up' ? 'bottom-full mb-2' : 'top-full mt-2',
                align === 'left' ? 'left-0' : 'right-0',
                direction === 'up'
                  ? align === 'left' ? 'origin-bottom-left' : 'origin-bottom-right'
                  : align === 'left' ? 'origin-top-left' : 'origin-top-right'
              )}
            >
              {LANGUAGES.map((item) => {
                const selected = item.code === lang;
                return (
                  <Menu.Item key={item.code}>
                    {({ active }) => (
                      <button
                        type="button"
                        lang={item.code}
                        onClick={() => setLang(item.code)}
                        aria-current={selected ? 'true' : undefined}
                        className={classNames(
                          'notranslate flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition',
                          active || selected ? 'bg-white/10 text-white' : 'text-white/75'
                        )}
                      >
                        <span className="w-7 text-xs font-bold tracking-[0.12em] text-white/50">
                          {item.label}
                        </span>
                        <span className="flex-1">{item.name}</span>
                        {selected ? (
                          <Check className="h-4 w-4 text-brand-soft" aria-hidden="true" />
                        ) : null}
                      </button>
                    )}
                  </Menu.Item>
                );
              })}
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
};

export default LanguageSwitcher;
